import type { SpotKey } from '../../core/spots';
import type { DrillShotClass } from '../../schemas/drill';

/**
 * Counting shots. Pure folds over attempts (voided ones are left out by the caller), shared by the
 * recorder, the results screens and the tests.
 */

export interface AttemptLike {
  id: string;
  playerId: string;
  spot: SpotKey | null;
  shotClass: DrillShotClass;
  made: boolean;
  clientTs?: number;
  seq?: number;
}

export interface SpotTally {
  makes: number;
  att: number;
}

export interface PlayerTally {
  playerId: string;
  makes: number;
  att: number;
  bySpot: Partial<Record<SpotKey, SpotTally>>;
  /** Shot by shot, oldest first. */
  results: boolean[];
  bestStreak: number;
}

/** Order for a fold: time, then the device's own sequence, then id. */
export function compareAttempts(a: AttemptLike, b: AttemptLike): number {
  return (
    (a.clientTs ?? 0) - (b.clientTs ?? 0) || (a.seq ?? 0) - (b.seq ?? 0) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
}

export function sortAttempts<T extends AttemptLike>(attempts: readonly T[]): T[] {
  return [...attempts].sort(compareAttempts);
}

/** makes / att in 0..1, or null when nothing was shot. */
export function rate(makes: number, att: number): number | null {
  return att > 0 ? makes / att : null;
}

/** "65%" for 0.65, "-" for null. */
export function percentText(p: number | null): string {
  return p === null ? '-' : `${Math.round(p * 100)}%`;
}

export function emptyTally(playerId: string): PlayerTally {
  return { playerId, makes: 0, att: 0, bySpot: {}, results: [], bestStreak: 0 };
}

/** One tally per player. `playerIds` fixes who is listed (and their order) even with no shots. */
export function tallyAttempts(
  attempts: readonly AttemptLike[],
  playerIds?: readonly string[],
): Map<string, PlayerTally> {
  const out = new Map<string, PlayerTally>();
  for (const id of playerIds ?? []) out.set(id, emptyTally(id));
  const streak = new Map<string, number>();
  for (const a of sortAttempts(attempts)) {
    let t = out.get(a.playerId);
    if (!t) {
      if (playerIds) continue;
      t = emptyTally(a.playerId);
      out.set(a.playerId, t);
    }
    t.att += 1;
    if (a.made) t.makes += 1;
    t.results.push(a.made);
    if (a.spot) {
      const s = t.bySpot[a.spot] ?? { makes: 0, att: 0 };
      s.att += 1;
      if (a.made) s.makes += 1;
      t.bySpot[a.spot] = s;
    }
    const run = a.made ? (streak.get(a.playerId) ?? 0) + 1 : 0;
    streak.set(a.playerId, run);
    if (run > t.bestStreak) t.bestStreak = run;
  }
  return out;
}

/** Group totals for a list of tallies. */
export function groupTotals(tallies: Iterable<PlayerTally>): SpotTally {
  let makes = 0;
  let att = 0;
  for (const t of tallies) {
    makes += t.makes;
    att += t.att;
  }
  return { makes, att };
}
